/**
 * SearchModal Component
 *
 * Global search modal accessible via:
 * - Header search icon click
 * - Cmd+K / Ctrl+K keyboard shortcut
 */

import { useEffect, useRef, useState } from 'react';
import { Search, Sparkles, X, Clock, TrendingUp, Loader2, ArrowRight } from 'lucide-react';
import {
  Button,
  Input,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@smallbiz/ui-primitives';
import { aiSearchClient, type AiSearchResult, type AiSearchError } from '../services/ai-search';
import { trackEvent } from '../lib/analytics';

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchModal({ open, onOpenChange }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AiSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AiSearchError | null>(null);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<string[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autocompleteAbortController = useRef<AbortController | null>(null);

  const popularSearches = [
    'Marketing strategies',
    'Sales funnel',
    'Cash flow management',
    'Leadership tips',
    'Email automation',
  ];

  useEffect(() => {
    if (open) {
      const stored = localStorage.getItem('ai_search_recent');
      if (stored) {
        try {
          setRecentSearches(JSON.parse(stored));
        } catch {
          // Invalid data, ignore
        }
      }

      setTimeout(() => inputRef.current?.focus(), 100);
      trackEvent('ai_search_opened', { source: 'header_modal' });
    } else {
      setQuery('');
      setResults(null);
      setError(null);
      setSelectedIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !results?.sources.length) return;

    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.sources.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        const source = results.sources[selectedIndex];
        handleResultClick(source.url, source.title, selectedIndex);
      }
    };

    document.addEventListener('keydown', handleKeyboard);
    return () => document.removeEventListener('keydown', handleKeyboard);
  }, [open, results, selectedIndex]);

  useEffect(() => {
    autocompleteAbortController.current?.abort();

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (query.trim().length < 3) {
      setAutocompleteSuggestions([]);
      setShowAutocomplete(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        const controller = new AbortController();
        autocompleteAbortController.current = controller;

        const res = await fetch(
          `/api/ai-search/suggestions?q=${encodeURIComponent(query.trim())}`,
          { signal: controller.signal }
        );
        if (res.ok) {
          const data = (await res.json()) as { suggestions?: string[] };
          const suggestions = data.suggestions || [];
          setAutocompleteSuggestions(suggestions);
          setShowAutocomplete(suggestions.length > 0);
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          setAutocompleteSuggestions([]);
          setShowAutocomplete(false);
        }
      }
    }, 500);

    return () => {
      autocompleteAbortController.current?.abort();
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [query]);

  const handleSearch = async (searchQuery?: string) => {
    const q = searchQuery || query;

    if (!q.trim()) {
      return;
    }

    setShowAutocomplete(false);
    setAutocompleteSuggestions([]);
    setLoading(true);
    setError(null);
    setResults(null);
    setSelectedIndex(-1);

    trackEvent('ai_search_query_submitted', {
      query: q,
      source: 'header_modal',
      query_length: q.length,
    });

    try {
      const result = await aiSearchClient.search(q, {
        generate_mode: 'none',
        display_mode: 'minimal',
      });

      setResults(result);

      const updated = [q, ...recentSearches.filter((s) => s !== q)].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem('ai_search_recent', JSON.stringify(updated));

      trackEvent('ai_search_completed', {
        query: q,
        result_count: result.result_count,
        latency_ms: result.latency_ms,
        source: 'header_modal',
      });

      if (result.result_count === 0) {
        trackEvent('ai_search_zero_results', { query: q });
      }
    } catch (err) {
      const searchError = err as AiSearchError;
      setError(searchError);

      trackEvent('ai_search_error', {
        query: q,
        error: searchError.error,
        status: searchError.status,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (url: string, title: string, index: number) => {
    trackEvent('ai_search_result_clicked', {
      url,
      title,
      index,
      source: 'header_modal',
    });

    window.open(url, '_blank', 'noopener,noreferrer');
    onOpenChange(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Search SmallBiz Innovation
          </DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search anything..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            className="pr-12"
          />
          <Button
            size="icon"
            variant="ghost"
            className="absolute right-1 top-1/2 -translate-y-1/2"
            onClick={() => handleSearch()}
            aria-label="Search"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>

        {showAutocomplete && autocompleteSuggestions.length > 0 && (
          <div className="mt-2 rounded-md border border-border bg-background shadow-sm">
            {autocompleteSuggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted"
              >
                <Sparkles className="h-4 w-4 text-primary" />
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {!query && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <Clock className="h-4 w-4" /> Recent searches
              </div>
              {recentSearches.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {recentSearches.map((search) => (
                    <Badge
                      key={search}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => handleSuggestionClick(search)}
                    >
                      {search}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No recent searches</p>
              )}
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                <TrendingUp className="h-4 w-4" /> Popular searches
              </div>
              <div className="flex flex-wrap gap-2">
                {popularSearches.map((search) => (
                  <Badge
                    key={search}
                    variant="outline"
                    className="cursor-pointer"
                    onClick={() => handleSuggestionClick(search)}
                  >
                    {search}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
            <p className="text-sm text-destructive">{error.message}</p>
          </div>
        )}

        {results && results.sources.length > 0 && (
          <div className="space-y-4">
            {results.sources.map((source, index) => (
              <button
                key={`${source.url}-${index}`}
                onClick={() => handleResultClick(source.url, source.title, index)}
                className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted ${
                  selectedIndex === index ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                <ArrowRight className="mt-1 h-4 w-4 text-primary" />
                <div>
                  <div className="text-sm font-semibold text-foreground">{source.title}</div>
                  {source.snippet && (
                    <div className="text-xs text-muted-foreground line-clamp-2">
                      {source.snippet}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {results && results.sources.length === 0 && !loading && !error && (
          <div className="rounded-lg border border-border bg-muted/40 p-4 text-center text-sm text-muted-foreground">
            No results found. Try a different query.
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Tip: Press Esc to close</span>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
